import { ReactNode, Suspense, type PropsWithChildren } from "react";
import { Text, View } from "react-native";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { FullPageLoadingScreen } from "@/shared/components/full-page-loading-screen";
import { Button } from "@/shared/components/ui/button";
import { Heading } from "@/shared/components/ui/heading";

function ErrorFallback({
  resetErrorBoundary,
}: {
  error: unknown;
  resetErrorBoundary: () => void;
}) {
  const [foreground] = useCSSVariable(["--color-foreground"]) as [string];

  return (
    <View className="bg-background flex-1 items-center justify-center gap-4 px-8">
      <View className="bg-destructive/15 h-16 w-16 items-center justify-center rounded-full">
        <Ionicons name="alert-circle-outline" size={36} color={foreground} />
      </View>
      <Heading variant="card" className="text-center">
        Algo deu errado
      </Heading>
      <Text className="text-muted-foreground text-center text-sm">
        Ocorreu um erro inesperado. Tente novamente ou volte mais tarde.
      </Text>
      <Button
        onPress={resetErrorBoundary}
        accessibilityLabel="Tentar novamente"
      >
        Tentar novamente
      </Button>
    </View>
  );
}

export function AppErrorBoundary({
  children,
  suspenseFallback = <FullPageLoadingScreen />,
}: PropsWithChildren<{ suspenseFallback?: ReactNode }>) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary onReset={reset} FallbackComponent={ErrorFallback}>
          <Suspense fallback={suspenseFallback}>{children}</Suspense>
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
